import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Route, NavLink, useLocation, useHistory, Switch } from 'react-router-dom';
import { DateTime } from 'luxon';
import { EditorState } from 'draft-js';
import { htmlFromEditorState, editorStateFromHtml } from "app/utils/DraftConvert";

import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import MenuList from '@material-ui/core/MenuList';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemText from '@material-ui/core/ListItemText';

import General from 'app/components/event/settings/General';
import Description from 'app/components/event/settings/Description';
import Invitations from 'app/components/event/settings/Invitations';
import Image from 'app/components/event/settings/Image';
import RedButton from 'app/components/buttons/RedButton';
import NewEventDialog from 'app/components/event/NewEventDialog';

import useApi from 'app/context/useApi';

import { containerFlex, containerFlexFixedSmall, containerFlexGrow} from 'app/styles/new-layout'

const useStyles = makeStyles(theme => ({
    container: {
        ...containerFlex(theme),
        padding: theme.spacing(3)
    },
    nav: {
        ...containerFlexFixedSmall(theme),
    },
    content: {
        ...containerFlexGrow(theme),
        paddingLeft: theme.spacing(3),

        [theme.breakpoints.down('sm')]: {
            paddingLeft: '0px',
        }
    },
    marginBottom: {
        marginBottom: theme.spacing(3)
    },
    marginBottomSmall: {
        marginBottom: theme.spacing(1)
    },
    menuItem: {
        borderRadius: theme.shape.borderRadius
    },
    saveButtonContainer: {
        marginTop: theme.spacing(3)
    },
    saveButton: {
        paddingLeft: theme.spacing(5),
        paddingRight: theme.spacing(5),
    },
}));

export default function Settings(props) {

    const { loading, setLoading, groups, getEvent } = props;

    let location = useLocation();
    const history = useHistory();
    const classes = useStyles();
    const api = useApi();

    const [ event, setEvent ] = useState(props.event);
    const [ errors, setErrors ] = useState({});
    const [ editorState, setEditorState ] = useState(EditorState.createEmpty());
    const [ newEventDialogOpen, setNewEventDialogOpen ] = useState(false);

    const eventId = props.event.id;
    const pathname = location.pathname;

    useEffect(() => {
        if (event.description) {
            setEditorState(editorStateFromHtml(event.description))
        }
    }, [event]);

    useEffect(() => {
        setEvent(props.event);
    }, [props.event])

    // handlers ------------------------------------------------

    const handleEvent = (name, e) => {

        let value;

        if (DateTime.isDateTime(e) || typeof e !== 'object') {
            value = e;
        } else {
            const target = e.target;
            value = (target.type === 'checkbox' ? target.checked : target.value);
        }

        if (isError(name)) delete errors[name];

        setEvent({ ...event, [name]: value });
    };

    const handleLocation = ac => {

        if (isError('location')) delete errors.location;
        if (isError('address')) delete errors.address;
        if (isError('city')) delete errors.city;
        if (isError('state')) delete errors.state;
        if (isError('zip')) delete errors.zip;
        if (isError('country')) delete errors.country;

        setEvent({
            ...event,
            location: ac.name,
            address: ac.address,
            city: ac.city,
            state: ac.state,
            zip: ac.zip,
            country: ac.country
        });
    }

    // Post Function ------------------------------------------

    const postEvent = () => {

        setLoading(true);

        const data = {...event};

        data.starts_at = event.starts_at.toSeconds();
        data.ends_at = event.ends_at.toSeconds();
        data.description = htmlFromEditorState(editorState)

        api.post(`/admin/event/${eventId}`, data)
            .then(() => getEvent())
            .catch(error => {
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
                setLoading(false);
            });
    }

    const postDraft = () => {

        setLoading(true);

        api.get(`/admin/event/${eventId}/draft`)
            .then(() => getEvent())
            .catch(error => {
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
                setLoading(false);
            });
    }

    const postPublish = () => {

        setLoading(true);

        api.get(`/admin/event/${eventId}/publish`)
            .then(() => getEvent())
            .catch(error => {
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
                setLoading(false);
            });
    }

    const deleteEvent = () => {

        setLoading(true);

        api.delete(`/admin/event/${eventId}`)
            .then(() => history.push('/admin/events'))
            .catch(error => {
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
                setLoading(false);
            });
    }

    // Errors ---------------------------------------------------

    const isError = field => {
        if (errors[field]) return true;
        return false;
    }

    const getErrorMessage = field => {
        if (isError(field)) return errors[field];
        return "";
    }

    // Dialogs ----------------------------------------------

    const openNewEventDialog = () => {
        setNewEventDialogOpen(true)
    }

    const closeNewEventDialog = () => {
        setNewEventDialogOpen(false);
    }

    // Constants ---------------------------------------------------

    const isSelected = tab => (tab === pathname);

    return (
        <div className={classes.container}>
            <div className={classes.nav}>
                <MenuList className={classes.marginBottom}>
                    <MenuItem
                        to={`/admin/events/${eventId}/settings`}
                        className={classes.menuItem}
                        selected={isSelected(`/admin/events/${eventId}/settings`)}
                        component={NavLink}
                        >
                        <ListItemText primary="General" />
                    </MenuItem>
                    <MenuItem
                        to={`/admin/events/${eventId}/settings/description`}
                        className={classes.menuItem}
                        selected={isSelected(`/admin/events/${eventId}/settings/description`)}
                        component={NavLink}
                        >
                        <ListItemText primary="Description" />
                    </MenuItem>
                    <MenuItem
                        to={`/admin/events/${eventId}/settings/invitations`}
                        className={classes.menuItem}
                        selected={isSelected(`/admin/events/${eventId}/settings/invitations`)}
                        component={NavLink}
                        >
                        <ListItemText primary="Invitations" />
                    </MenuItem>
                    <MenuItem
                        to={`/admin/events/${eventId}/settings/image`}
                        className={classes.menuItem}
                        selected={isSelected(`/admin/events/${eventId}/settings/image`)}
                        component={NavLink}
                        >
                        <ListItemText primary="Image" />
                    </MenuItem>
                </MenuList>
                {event.published ? (
                    <Button
                        fullWidth
                        color="primary"
                        variant="outlined"
                        onClick={postDraft}
                        className={classes.marginBottomSmall}
                        >
                        Set as Draft
                    </Button>
                ) : (
                    <Button
                        fullWidth
                        color="primary"
                        variant="contained"
                        className={classes.marginBottomSmall}
                        onClick={postPublish}
                        >
                        Publish Now
                    </Button>
                )}
                <Button
                    fullWidth
                    variant="outlined"
                    onClick={openNewEventDialog}
                    className={classes.marginBottomSmall}
                    >
                    Copy Event
                </Button>
                <RedButton
                    fullWidth
                    variant="outlined"
                    onClick={deleteEvent}
                    >
                    Delete Event
                </RedButton>
            </div>
            <div className={classes.content}>
                <Switch>
                    <Route path={`/admin/events/${eventId}/settings`} exact>
                        <General
                            event={event}
                            handleEvent={handleEvent}
                            handleLocation={handleLocation}
                            isError={isError}
                            getErrorMessage={getErrorMessage}
                            errors={errors}
                        />
                    </Route>
                    <Route path={`/admin/events/${eventId}/settings/description`} exact>
                        <Description
                            event={event}
                            handleEvent={handleEvent}
                            isError={isError}
                            getErrorMessage={getErrorMessage}
                            errors={errors}
                            editorState={editorState}
                            setEditorState={setEditorState}
                        />
                    </Route>
                    <Route path={`/admin/events/${eventId}/settings/invitations`} exact>
                        <Invitations
                            event={event}
                            groups={groups}
                            handleEvent={handleEvent}
                            isError={isError}
                            getErrorMessage={getErrorMessage}
                            errors={errors}
                            loading={loading}
                        />
                    </Route>
                    <Route path={`/admin/events/${eventId}/settings/image`} exact>
                        <Image
                            event={event}
                            getEvent={getEvent}
                            loading={loading}
                            setLoading={setLoading}
                        />
                    </Route>
                </Switch>
                {! isSelected(`/admin/events/${eventId}/settings/image`) &&
                    <Typography align="right" component="div" className={classes.saveButtonContainer}>
                        <Button
                            disabled={loading}
                            variant="contained"
                            color="primary"
                            onClick={postEvent}
                            className={classes.saveButton}
                            size="small"
                            >
                            save
                        </Button>
                    </Typography>
                }
            </div>
            <NewEventDialog
                open={newEventDialogOpen}
                onClose={closeNewEventDialog}
                copy={event}
            />
        </div>
    );
}
